// Mane product surfaces shown in the homepage carousel.
// Each entry is a promotional tile (image now, video later) that opens a rich drawer.
// Headlines use inline <em> to flip to serif italic (see global typography rules).

import engineeringImg from "@/assets/capabilities/engineering.jpg";
import securityImg from "@/assets/capabilities/security.jpg";
import marketingImg from "@/assets/capabilities/marketing.jpg";
import designImg from "@/assets/capabilities/design.jpg";
import operationsImg from "@/assets/capabilities/operations.jpg";
import salesImg from "@/assets/capabilities/sales.jpg";
import supportImg from "@/assets/capabilities/support.jpg";
import researchImg from "@/assets/capabilities/research.jpg";
import customerSuccessImg from "@/assets/capabilities/customer-success.jpg";

export type ProductChipTone =
  | "roster"
  | "connector"
  | "security"
  | "creative"
  | "persona"
  | "operator"
  | "growth"
  | "conversational"
  | "realtime";

export type ProductBadge = "NEW" | "TRENDING";

export type Product = {
  id: string;
  slug: string;
  /** Type chip shown top-right on the card. */
  chip: { label: string; tone: ProductChipTone };
  badge?: ProductBadge;
  /** Short product name shown as eyebrow / kicker. */
  name: string;
  /** Headline with optional <em> for serif italic accent. */
  headline: string;
  /** One-line subhead. */
  tagline: string;
  /** Card image (will be replaced with looping mp4 later). */
  image: string;
  /** Optional looping video URL. When present, replaces the image on the card. */
  videoSrc?: string;
  /** Italic lede in the drawer hero. */
  mission: string;
  /** Paragraph under the lede. */
  pitch: string;
  /** "What's inside" 3-up grid. */
  inside: { label: string; description: string; href?: string }[];
  /** Three demo prompts → outcomes. */
  flows: { prompt: string; outcome: string }[];
  /** Runtime mark slugs to show in "Where it runs". */
  runsOn: string[];
  /** Three metric chips. */
  metrics: { value: string; label: string }[];
  sampleBriefs: string[];
  deliverables: string[];
  /** Optional deeper lander link. */
  landerHref?: string;
  landerLabel?: string;
};

const ALL_RUNTIMES = [
  "Claude Code",
  "Codex",
  "Cursor",
  "Antigravity",
  "Grok",
  "Manus",
  "OpenClaw",
  "Hermes",
  "Replit",
  "Perplexity",
  "Lovable",
];

export const PRODUCTS: Product[] = [
  {
    id: "role-agents",
    slug: "role-agents",
    chip: { label: "Agents", tone: "roster" },
    name: "Role Agents",
    videoSrc: "/product-videos/hire-never-logs-off.mp4",
    headline: "The hire that <em>never logs off</em>.",
    tagline: "Outcome-based agents for every role.",
    image: engineeringImg,
    mission:
      "Stop prompting. Hire a role. Each agent owns an outcome, not a chat window.",
    pitch:
      "Pick the role you would have posted on LinkedIn. Mane spins up the agent with the right tools, memory, and SOPs, then operates it inside the systems your team already uses.",
    inside: [
      { label: "Engineering", description: "Tickets in, reviewed PRs out.", href: "/agents#engineering" },
      { label: "Sales & SDR", description: "Pipeline researched, sequenced, replied to.", href: "/agents#sales" },
      { label: "Customer Success", description: "Health scores, QBRs, save plays.", href: "/agents#customer-success" },
      { label: "Support", description: "Tier 1 closed, Tier 2 drafted, escalations summarised.", href: "/agents#support" },
      { label: "Ops & Finance", description: "Reconciliations, vendor reviews, weekly close.", href: "/agents#operations" },
      { label: "Marketing & Design", description: "Briefs to assets, on brand, on cadence.", href: "/agents#marketing" },
    ],
    flows: [
      { prompt: "Hire an SDR for our outbound to RevOps leaders.", outcome: "ICP built, 200 accounts enriched, 3-touch sequence live by Friday." },
      { prompt: "Add SSO via Okta to the admin app.", outcome: "Scoped PR with tests, security notes, screenshots, waiting on your merge." },
      { prompt: "Close last month's books.", outcome: "Reconciliation pack, variance commentary, anomalies flagged for review." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "23", label: "Specialist roles" },
      { value: "L2-L3", label: "Autonomy ladder" },
      { value: "24/7", label: "Always on" },
    ],
    sampleBriefs: [
      "Hire an account executive for the EMEA mid-market segment",
      "Pick up the on-call queue tonight and file morning brief",
      "Run the weekly content calendar end to end",
    ],
    deliverables: [
      "A named agent per role with its own SOPs",
      "Shared memory across the whole team",
      "Weekly digest of work shipped, per role",
    ],
    landerHref: "/agents",
    landerLabel: "Open all agents",
  },
  {
    id: "mane-mcp",
    slug: "mane-mcp",
    chip: { label: "Connector", tone: "connector" },
    badge: "NEW",
    name: "Mane MCP",
    videoSrc: "/product-videos/plug-any-agent.mp4",
    headline: "Plug into <em>any agent</em>.",
    tagline: "Memory, skills, and roles. Anywhere.",
    image: researchImg,
    mission:
      "Mane runs as MCP. Any agent runtime that speaks MCP can call our brain, our skills, and our roster.",
    pitch:
      "Your existing IDE agent stays. Mane shows up as tools. Memory follows the user across runtimes so your Claude session, your Codex run, and your Cursor chat all share the same institutional brain.",
    inside: [
      { label: "Brain MCP", description: "Persistent, project-scoped memory shared across every agent." },
      { label: "Skills MCP", description: "200+ skills your agent can install on demand." },
      { label: "Agents MCP", description: "Delegate a sub-task to a specialist role from inside any chat." },
      { label: "Browser MCP", description: "Authenticated browsing with session reuse and cleanup." },
      { label: "Files MCP", description: "Project files, attachments, and exports across runtimes." },
      { label: "Inbox MCP", description: "Slack, email, and DMs as first-class tools." },
    ],
    flows: [
      { prompt: "@mane recall everything we know about Acme.", outcome: "Brain returns prior threads, contracts, and the open save play." },
      { prompt: "@mane install the SOC2 audit skill.", outcome: "Skill loaded, controls listed, evidence collector running." },
      { prompt: "@mane hand this to the legal agent.", outcome: "Legal role picks up the redline, returns clean diff with rationale." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "11", label: "Runtimes supported" },
      { value: "200+", label: "Skills" },
      { value: "1 install", label: "Works everywhere" },
    ],
    sampleBriefs: [
      "Add the Mane connector to my Claude desktop",
      "Wire Mane into our Cursor team workspace",
      "Expose only the research and writing skills to my Codex run",
    ],
    deliverables: [
      "MCP endpoint per workspace",
      "Per-runtime install snippet",
      "Audit log of every tool call",
    ],
    landerHref: "/mcp",
    landerLabel: "Open the MCP page",
  },
  {
    id: "agent-antivirus",
    slug: "agent-antivirus",
    chip: { label: "Security", tone: "security" },
    name: "Spartan",
    videoSrc: "/product-videos/spartan.mp4",
    headline: "<em>Protection</em> in the agentic world.",
    tagline: "Antivirus and cybersecurity for the agentic world. Per-tool firewall, sandboxing, fleet console.",
    image: securityImg,
    mission:
      "Your computer now runs agents that read your inbox, your repo, and your card. Spartan is the antivirus built for that machine.",
    pitch:
      "A lightweight endpoint agent that sits on every device running Claude Code, Codex, Cursor, or any MCP runtime. Monitors every tool call, file touched, and network hop. Blocks exfiltration, isolates suspicious runs, and gives IT a single pane of glass across the fleet.",
    inside: [
      { label: "Endpoint daemon", description: "Tiny background process. Watches every agent process on the machine." },
      { label: "Behavior engine", description: "Flags injection, exfil, and runaway tool loops in real time." },
      { label: "Secret guard", description: "Catches tokens and keys in prompts, outputs, and clipboard pulls." },
      { label: "Tool firewall", description: "Allow or deny per tool, per project, per runtime." },
      { label: "Run sandboxing", description: "Browser, files, and shell isolated per agent session." },
      { label: "Fleet console", description: "One view of every machine, every agent, every incident." },
    ],
    flows: [
      { prompt: "An agent on a laptop tried to push secrets to a public repo.", outcome: "Push blocked at the endpoint, secret rotated, incident filed in the fleet console." },
      { prompt: "A Cursor run started exfiltrating files over an unknown domain.", outcome: "Network call killed, run quarantined, IT pinged with a replay." },
      { prompt: "New MCP skill requested shell + network access on a dev box.", outcome: "Granted only inside a sandboxed run, capability scope shown to the user." },
    ],
    runsOn: ["Claude Code", "Codex", "Cursor", "Antigravity", "Hermes", "Replit"],
    metrics: [
      { value: "Real-time", label: "On-device monitoring" },
      { value: "100%", label: "Runs audited" },
      { value: "1-click", label: "Quarantine & undo" },
    ],
    sampleBriefs: [
      "Block any agent on the fleet from touching production databases",
      "Quarantine the run that emailed our customer list",
      "Give IT a weekly agentic-endpoint digest",
    ],
    deliverables: [
      "Endpoint daemon for macOS, Windows, Linux",
      "Per-workspace policy file synced across devices",
      "Fleet console with replayable incidents",
    ],
  },
  {
    id: "content-studio",
    slug: "content-studio",
    chip: { label: "Creative", tone: "creative" },
    name: "Content Studio",
    videoSrc: "/product-videos/viral-autopilot.mp4",
    headline: "Creative agents with skills, wired to the best <em>video and image</em> models.",
    tagline: "Wired to Gemini Omni, Seedance 2.0, GPT 2.0, Grok.",
    image: marketingImg,
    mission:
      "A creative team that ships every day. Drop a brief. Mane's creative agents research the angle, write the scripts, generate the visuals, edit the cuts, and post to every channel, on brand.",
    pitch:
      "Each agent comes loaded with skills (hook writing, storyboarding, editing, scheduling) and routes work to the frontier models: Gemini Omni, Seedance 2.0, GPT 2.0, and Grok. You get cinema-grade video, editorial photography, and scroll-stopping carousels. Auto-cut for TikTok, Reels, Shorts, LinkedIn, and X.",
    inside: [
      { label: "Hook factory", description: "20 hook variants per topic, ranked by your historic performance." },
      { label: "Video gen", description: "9:16, 1:1, 16:9 cuts with captions and beats." },
      { label: "Photo & carousels", description: "Editorial photo and 10-slide carousels in your brand system." },
      { label: "Voice match", description: "Trained on your back catalogue. Sounds like you, not the model." },
      { label: "Auto-schedule", description: "Direct to TikTok, Reels, Shorts, LinkedIn, X with the right specs." },
      { label: "Performance loop", description: "Reads analytics, keeps what works, kills what doesn't." },
    ],
    flows: [
      { prompt: "Make a week of TikToks about our Series A.", outcome: "7 scripts, 7 videos, 7 carousels, scheduled in your calendar." },
      { prompt: "Cut my podcast into 10 viral clips.", outcome: "Top moments scored, captioned, exported in 9:16 and 1:1." },
      { prompt: "Refresh our IG grid for the launch.", outcome: "9-tile editorial grid, on brand, with copy variants." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "7 days", label: "Content per brief" },
      { value: "5 channels", label: "Auto-distributed" },
      { value: "On brand", label: "Trained on your refs" },
    ],
    sampleBriefs: [
      "Repurpose this 60-minute webinar into a month of content",
      "Run our weekly TikTok cadence, end to end",
      "Build the launch trailer for our new feature",
    ],
    deliverables: [
      "Ready-to-post videos, photos, and carousels",
      "Scheduled posts per channel",
      "Weekly performance report with next-week plan",
    ],
  },
  {
    id: "ai-influencer",
    slug: "ai-influencer",
    chip: { label: "Persona", tone: "persona" },
    name: "AI Influencer",
    videoSrc: "/product-videos/ai-influencer.mp4",
    headline: "A creator that <em>never sleeps</em>.",
    tagline: "An AI persona that creates, markets, and sells. Always on brand.",
    image: customerSuccessImg,
    mission:
      "Build a character once. Mane keeps the look, the voice, the lore, and ships content as that character, forever.",
    pitch:
      "Lock the face, the wardrobe, the voice, and the worldview. The agent generates content in character, runs the inbox in character, and learns from what its audience actually rewards.",
    inside: [
      { label: "Character lock", description: "Face, wardrobe, lighting, and voice kept consistent shoot to shoot." },
      { label: "Lore memory", description: "Backstory, opinions, and timeline tracked across every post." },
      { label: "Daily shoots", description: "New content generated to a brief, on schedule." },
      { label: "DM autopilot", description: "Inbox handled in character, with sales handoffs to humans." },
      { label: "Brand deals", description: "Drafts pitches, negotiates, files the contract." },
      { label: "Multi-character lineup", description: "Run a stable of creators from one workspace." },
    ],
    flows: [
      { prompt: "Post 3 reels a day as Luna for the next month.", outcome: "90 reels generated, scheduled, in voice, with captions." },
      { prompt: "Reply to every DM, sell the merch link if asked.", outcome: "Inbox answered in character, sales flagged to your team." },
      { prompt: "Pitch 10 brands that fit Luna's aesthetic.", outcome: "Personalised pitches sent, replies triaged, calls booked." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "1 character", label: "Infinite content" },
      { value: "Always on", label: "DMs answered" },
      { value: "In voice", label: "Every post" },
    ],
    sampleBriefs: [
      "Launch a virtual creator for our skincare brand",
      "Spin up a niche fitness character on TikTok",
      "Run our founder's likeness as a content engine, with approvals",
    ],
    deliverables: [
      "Locked character kit",
      "Daily content + scheduled posts",
      "Inbox + brand-deal pipeline",
    ],
  },
  {
    id: "autonomous-business",
    slug: "autonomous-business",
    chip: { label: "Operator", tone: "operator" },
    name: "Autonomous Business",
    videoSrc: "/product-videos/new-business-verticals.mp4",
    headline: "<em>New business</em> or verticals.",
    tagline: "Sites, product ops, A/B tests, GTM. Built and run.",
    image: operationsImg,
    mission:
      "Give Mane a thesis. Get back a live business or a new vertical, hosted, marketed, and operated, with a P&L you can read every Monday.",
    pitch:
      "Picks the niche, builds the site, ships the product ops, runs the A/B tests, executes the GTM, answers support, and reports the numbers. You stay on strategy. Mane runs the shop.",
    inside: [
      { label: "Offer & positioning", description: "Niche scored, ICP defined, offer drafted with pricing." },
      { label: "Sites & checkout", description: "Storefronts, landing pages, checkout, and emails live in days." },
      { label: "Product ops", description: "Catalog, fulfillment, vendors, refunds, daily ops on rails." },
      { label: "A/B tests", description: "Hypotheses queued, variants shipped, winners promoted weekly." },
      { label: "GTM execution", description: "Paid + organic plan executed across every channel." },
      { label: "Weekly P&L", description: "Revenue, spend, margin, and next-week plan in one page." },
    ],
    flows: [
      { prompt: "Launch a $39 ebook on running a 5-person studio.", outcome: "Book drafted, cover designed, site live, ads running on Monday." },
      { prompt: "Spin up a Shopify store for ceramic mugs.", outcome: "Storefront live, 12 SKUs, supplier briefed, first orders booked." },
      { prompt: "Run a 6-week cohort on prompt engineering.", outcome: "Curriculum, landing, payments, Discord, and weekly ops covered." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "Days", label: "From idea to live" },
      { value: "1 page", label: "Weekly P&L" },
      { value: "End to end", label: "Built + run" },
    ],
    sampleBriefs: [
      "Launch a niche newsletter on agentic security",
      "Build and run a print-on-demand brand for runners",
      "Spin up a course on Mane, ship it in 4 weeks",
    ],
    deliverables: [
      "Live storefront or site with payments",
      "Asset library (book, course, or SKUs)",
      "Weekly ops + P&L report",
    ],
  },
  {
    id: "ad-suite",
    slug: "ad-suite",
    chip: { label: "Growth", tone: "growth" },
    name: "Ad Suite",
    videoSrc: "/product-videos/media-buyer.mp4",
    headline: "The media buyer <em>in your stack</em>.",
    tagline: "Writes, buys, tests, scales.",
    image: salesImg,
    mission:
      "An always-on growth team. Mane writes the creative, structures the accounts, runs the tests, and reallocates the budget every morning.",
    pitch:
      "Authenticated into your ad accounts. Builds the campaign tree, generates 50 creatives a week, kills the losers, scales the winners, and reports the math your CFO actually wants.",
    inside: [
      { label: "Creative engine", description: "50 ad variants/week across video, static, and UGC." },
      { label: "Account structure", description: "Campaigns, ad sets, and audiences built to your CAC target." },
      { label: "Test orchestration", description: "Hooks, offers, and audiences A/B/n tested with stat-sig calls." },
      { label: "Budget allocator", description: "Reallocates daily across channels based on MER and payback." },
      { label: "Compliance check", description: "Claims, disclosures, and platform rules pre-flighted." },
      { label: "Exec report", description: "MER, CAC, payback, and incrementality, in one weekly deck." },
    ],
    flows: [
      { prompt: "Hit a 3x MER on Meta this month.", outcome: "Account restructured, 60 creatives shipped, MER tracking 3.1x." },
      { prompt: "Launch a new offer across Meta, Google, and TikTok.", outcome: "Campaigns live in 48 hours, daily reallocation, weekly readout." },
      { prompt: "Cut wasted spend.", outcome: "Bottom-decile ads paused, budget moved, $14k/wk recovered." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "50 / wk", label: "Creatives shipped" },
      { value: "Daily", label: "Budget reallocation" },
      { value: "MER + CAC", label: "Reported, not vibes" },
    ],
    sampleBriefs: [
      "Take over our Meta account and hit 3x ROAS",
      "Spin up a TikTok creative lab for our DTC brand",
      "Run a brand-lift test on our YouTube spend",
    ],
    deliverables: [
      "Restructured ad accounts",
      "Weekly creative drops",
      "Exec-ready growth report",
    ],
  },
  {
    id: "consumer-agents",
    slug: "consumer-agents",
    chip: { label: "Conversational", tone: "conversational" },
    name: "Consumer Agents",
    videoSrc: "/product-videos/agent-faces-customers.mp4",
    headline: "<em>Customer facing</em> agents.",
    tagline: "DMs, chat, upsells, bookings.",
    image: supportImg,
    mission:
      "Your storefront, your inbox, your DMs, staffed by a character your customers actually want to talk to.",
    pitch:
      "Pick the personality. Wire the catalog, the FAQ, and the booking system. Mane runs the chat across web, WhatsApp, IG, and SMS, with handoffs to humans when the deal needs a person.",
    inside: [
      { label: "Persona designer", description: "Pick the voice, the tone, the catchphrases, the manners." },
      { label: "Catalog awareness", description: "Knows every SKU, price, stock level, and shipping window." },
      { label: "Upsell & bundles", description: "Reads intent, suggests the right add-on, closes the cart." },
      { label: "Booking & quotes", description: "Holds calendar slots, sends quotes, takes deposits." },
      { label: "Multi-channel", description: "Web chat, WhatsApp, IG DM, SMS, all from one brain." },
      { label: "Human handoff", description: "Hot leads routed to your team with the full transcript." },
    ],
    flows: [
      { prompt: "Recover abandoned carts on WhatsApp.", outcome: "Personalised follow-ups sent, 22% of carts recovered, paid out." },
      { prompt: "Be the front desk for our med-spa DMs.", outcome: "Booked 14 appointments overnight, flagged 2 for a human call." },
      { prompt: "Upsell our protein stack to repeat buyers.", outcome: "Targeted offers sent, AOV up 18% on the cohort." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "5 channels", label: "Web, WA, IG, SMS, email" },
      { value: "24/7", label: "Always answering" },
      { value: "Brand-safe", label: "Persona-locked" },
    ],
    sampleBriefs: [
      "Staff our IG DMs as our founder's voice",
      "Run live chat on our Shopify store with upsell logic",
      "Be the WhatsApp concierge for our hotel",
    ],
    deliverables: [
      "Persona spec + guardrails",
      "Wired multi-channel inbox",
      "Daily handoff + revenue report",
    ],
  },
  {
    id: "voice-avatar",
    slug: "voice-avatar",
    chip: { label: "Realtime", tone: "realtime" },
    badge: "NEW",
    name: "Voice & Avatar",
    videoSrc: "/product-videos/voice-avatar.mp4",
    headline: "<em>Voice, SMS, video</em> agents for customer interactions.",
    tagline: "Phone, SMS, video, and avatar agents that pick up, dial out, and show up on the web.",
    image: designImg,
    mission:
      "Realtime agents that pick up the phone, reply to texts, host a video call, or staff your front desk, with a face and a voice that is yours.",
    pitch:
      "Inbound and outbound calling, two-way SMS, video meetings, and on-screen avatars. Trained on your playbook, your objections, your tone. Hands off to humans when the call actually needs one.",
    inside: [
      { label: "Inbound phone", description: "Answers every call, qualifies, books, transfers." },
      { label: "Outbound dialer", description: "Runs lists, leaves voicemails, books meetings." },
      { label: "SMS concierge", description: "Two-way texting for reminders, confirmations, and quick replies." },
      { label: "Video avatar", description: "On-screen presence for web meetings and explainers." },
      { label: "Voice clone", description: "Founder or rep voice, with consent and safeguards." },
      { label: "Live transcripts", description: "Searchable, summarised, and pushed to your CRM." },
      { label: "Sentiment escalation", description: "Frustrated caller? Routed to a human in seconds." },
    ],
    flows: [
      { prompt: "Take after-hours support calls for our SaaS.", outcome: "94% resolved without escalation, summaries in Zendesk." },
      { prompt: "Run an outbound day across calls and SMS.", outcome: "1,200 dials, 800 texts, 84 conversations, 19 meetings booked." },
      { prompt: "Host our first-touch demo as an avatar.", outcome: "Branded avatar runs the demo, hands off to AE for pricing." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "<500ms", label: "Voice latency" },
      { value: "Voice + SMS + video", label: "Full coverage" },
      { value: "Your voice", label: "Cloned, with consent" },
    ],
    sampleBriefs: [
      "Cover our front desk between 6pm and 8am",
      "Run an outbound day on this 1,000-lead list",
      "Host our weekly product demo as an avatar",
    ],
    deliverables: [
      "Phone numbers + dialer access",
      "Voice + avatar kit",
      "CRM-synced transcripts and summaries",
    ],
  },
  {
    id: "gtm-agent",
    slug: "gtm-agent",
    chip: { label: "Growth", tone: "growth" },
    badge: "NEW",
    name: "GTM Agent",
    videoSrc: "/product-videos/gtm-agent.mp4",
    headline: "<em>Sells</em> the business. Every day.",
    tagline: "GTM, CRM, lead gen, SDR. One agent that prospects, pitches, and promotes.",
    image: salesImg,
    mission:
      "A full go-to-market team in one agent, prospecting, pitching, nurturing, and promoting your business while your humans focus on closing.",
    pitch:
      "Builds lists, enriches leads, runs outbound across email and LinkedIn, books meetings, keeps the CRM clean, and ships campaign content on cadence. Plugs into your stack, learns your offer, and answers to a weekly number.",
    inside: [
      { label: "Lead gen", description: "Sources, enriches, and scores ICP accounts every day." },
      { label: "SDR motion", description: "Multi-channel outbound, personalised, sequenced, replied to." },
      { label: "CRM hygiene", description: "Dedupes, updates stages, logs activity, flags rot." },
      { label: "Content engine", description: "Posts, newsletters, case studies, shipped on cadence." },
      { label: "Pipeline ops", description: "Forecast, stuck-deal nudges, win/loss notes back to product." },
      { label: "Reporting", description: "Weekly GTM scorecard, by channel, by rep, by segment." },
    ],
    flows: [
      { prompt: "Find and warm 200 ICP accounts this week.", outcome: "214 sourced, 138 enriched, 41 replies, 12 meetings booked." },
      { prompt: "Clean the CRM and reforecast Q2.", outcome: "1,820 records deduped, 312 stages corrected, forecast tightened by 9%." },
      { prompt: "Run a 4-week launch campaign for the new vertical.", outcome: "Landing page, 12 posts, 4 emails, 2 case studies, shipped on cadence." },
    ],
    runsOn: ALL_RUNTIMES,
    metrics: [
      { value: "Daily", label: "Prospecting cadence" },
      { value: "CRM-native", label: "HubSpot, Salesforce, Attio" },
      { value: "Weekly", label: "GTM scorecard" },
    ],
    sampleBriefs: [
      "Build pipeline for our new mid-market segment",
      "Run SDR motion across email and LinkedIn",
      "Promote the launch with a 4-week content plan",
    ],
    deliverables: [
      "Sourced + enriched lead lists",
      "Live outbound sequences with replies",
      "Weekly GTM scorecard + pipeline report",
    ],
  },
];
